import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Copy, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BuyerNFTDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [nft, setNft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchNFTDetail();
  }, [id]);

  const fetchNFTDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/buyer/nfts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNft(res.data.nft || res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching NFT detail:', err);
      setError(err.response?.data?.detail || 'Failed to load NFT details');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#0B0B0B' }}>
        Loading NFT details...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px' }}>
        <button
          onClick={() => navigate('/buyer/dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: 'none',
            color: '#4B9DFF',
            cursor: 'pointer',
            marginBottom: '24px',
            fontSize: '14px'
          }}
        >
          <ArrowLeft size={18} /> Back to Dashboard
        </button>
        <div style={{
          background: 'rgba(255,107,107,0.1)',
          border: '1px solid rgba(255,107,107,0.3)',
          borderRadius: '12px',
          padding: '24px',
          color: '#FF6B6B'
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (!nft) {
    return (
      <div style={{ padding: '40px', color: '#999' }}>
        NFT not found
      </div>
    );
  }

  return (
    <div style={{ padding: '40px' }}>
      <button
        onClick={() => navigate('/buyer/dashboard')}
        data-testid="back-to-dashboard"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          color: '#4B9DFF',
          cursor: 'pointer',
          marginBottom: '24px',
          fontSize: '14px'
        }}
      >
        <ArrowLeft size={18} /> Back to Dashboard
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {/* NFT Image */}
        <div style={{
          background: '#FFFFFF',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid #E6E8EF'
        }}>
          <img
            src={nft.image_url}
            alt={nft.portfolio_name}
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
            onError={(e) => {
              e.target.style.background = 'linear-gradient(135deg, #7C3AED, #4B9DFF)';
              e.target.style.height = '400px';
            }}
          />
        </div>

        {/* NFT Details */}
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0B0B0B', marginBottom: '8px' }}>
            {nft.portfolio_name}
          </h1>
          <p style={{ fontSize: '16px', color: '#999', marginBottom: '24px' }}>
            Token #{nft.serial || nft.token_id}
          </p>

          <div style={{
            background: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid #E6E8EF',
            marginBottom: '16px'
          }}>
            <h3 style={{ fontSize: '14px', color: '#999', marginBottom: '16px' }}>Token Details</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>NFT ID</div>
              <div style={{ fontSize: '14px', color: '#0B0B0B', fontFamily: 'monospace' }}>{nft.id}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Token ID</div>
              <div style={{ fontSize: '14px', color: '#0B0B0B' }}>{nft.token_id}</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Ownership</div>
              <div data-testid="nft-detail-ownership" style={{ fontSize: '14px', fontWeight: 700, color: nft.ownership_status === 'verified_onchain' ? '#065F46' : '#92400E' }}>
                {nft.ownership_label || 'Not verified'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{nft.ownership_note} Record status: {nft.status}.</div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Contract Address</div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontSize: '14px', 
                color: '#0B0B0B',
                fontFamily: 'monospace'
              }}>
                {nft.contract_address?.substring(0, 12)}...{nft.contract_address?.substring(nft.contract_address.length - 8)}
                <button
                  onClick={() => copyToClipboard(nft.contract_address)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  {copied ? <Check size={16} color="#47FFB0" /> : <Copy size={16} color="#4B9DFF" />}
                </button>
                <button
                  onClick={() => window.open(`https://amoy.polygonscan.com/address/${nft.contract_address}`, '_blank')}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  <ExternalLink size={16} color="#4B9DFF" />
                </button>
              </div>
            </div>

            {nft.acquired_at && (
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Acquired</div>
                <div style={{ fontSize: '14px', color: '#0B0B0B' }}>
                  {new Date(nft.acquired_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            )}
          </div>

          {nft.utilities_count > 0 && (
            <div style={{
              background: 'rgba(71, 255, 176, 0.1)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid rgba(71, 255, 176, 0.3)'
            }}>
              <div style={{ color: '#47FFB0', fontSize: '14px', fontWeight: 600 }}>
                {nft.utilities_count} Utilities Available
              </div>
              <div style={{ color: '#999', fontSize: '13px', marginTop: '4px' }}>
                Check the Utilities tab for exclusive benefits
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyerNFTDetail;
